"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Result = void 0;
/**
 * An object which can either be in an `Ok` state, or an `Err` state, with an associated value, `T`
 */
var Result = /** @class */ (function () {
    function Result(tuple) {
        this.tuple = tuple;
    }
    /**
     * Contructs a {@link Result} object in the `Ok` state around the given value.
     *
     * @example
     * ```ts
     * console.log(Result.Ok('foo').isOk()); // true
     * ```
     */
    Result.Ok = function (v) {
        return new Result([true, v]);
    };
    /**
     * Constructs a {@link Result} object in the `Err` state around the given value.
     *
     * @example
     * ```ts
     * console.log(Result.Err('yikes! something went wrong').isOk()); // false
     */
    Result.Err = function (v) {
        return new Result([false, v]);
    };
    Result.from = function (fn, mapErr) {
        var _a, _b, _c, _d;
        try {
            return Result.Ok(fn());
        }
        catch (err) {
            if (err instanceof Error)
                return Result.Err((_a = mapErr === null || mapErr === void 0 ? void 0 : mapErr(err)) !== null && _a !== void 0 ? _a : err.toString());
            return Result.Err((_c = mapErr === null || mapErr === void 0 ? void 0 : mapErr(new Error((_b = err === null || err === void 0 ? void 0 : err.toString()) !== null && _b !== void 0 ? _b : "null"))) !== null && _c !== void 0 ? _c : new Error((_d = err === null || err === void 0 ? void 0 : err.toString()) !== null && _d !== void 0 ? _d : "null").toString());
        }
    };
    Result.prototype.inner = function () {
        return this.tuple[1];
    };
    /**
     * Transforms a `Result<T, E>` into a `Result<J, K>` by specifying a mapper function `(v: T) => J` and
     * `(v: E) => K`.
     *
     * @example
     * ```ts
     * const res: Result<number, { msg: string }> = ...;
     * const mapped: Result<string, string> = res.map((v: number) => v.toString(), JSON.stringify);
     * ```
     */
    Result.prototype.map = function (mapOk, mapErr) {
        if (this.tuple[0])
            return Result.Ok(mapOk(this.tuple[1]));
        else
            return Result.Err(mapErr(this.tuple[1]));
    };
    /**
     * Returns `true` if the {@link Result} is in an `Ok` state.
     *
     * @example
     * ```ts
     * const res: Result<string, string> = ...;
     * if (res.isOk()) console.log('passed!');
     * ```
     */
    Result.prototype.isOk = function () {
        return this.tuple[0];
    };
    /**
     * Returns `true` is the {@link Result} in in an `Err` state.
     *
     * @example
     * ```ts
     * const res: Result<string, string> = ...;
     * if (res.isErr()) console.log('an error occured!');
     * ```
     */
    Result.prototype.isErr = function () {
        return !this.tuple[0];
    };
    /**
     * Returns the inner value if the {@link Result} in in an `Ok` state, otherwise `null`
     *
     * @example
     * ```ts
     * const res: Result<string, string> = ...;
     * if (res.isOk()) console.log(`result: ${res.ok()}`);
     * ```
     */
    Result.prototype.ok = function () {
        if (this.isErr())
            return null;
        else
            return this.inner();
    };
    /**
     * Returns the inner value of the {@link Result} in an `Err` state, otherwise `null`
     *
     * @example
     * ```ts
     * const res: Result<string, string> = ...;
     * if (res.isErr()) console.log(`result failed with: ${res.err()}`);
     * ```
     */
    Result.prototype.err = function () {
        if (this.isOk())
            return null;
        else
            return this.inner();
    };
    /**
     * Maps a `Result.Ok(null)` to `null`, otherwise return self.
     *
     * @example
     * ```ts
     * const res: Result<Maybe<string>, string> = ...;
     * const m: Maybe<Result<string, string>> = res.transpose();
     * ```
     */
    Result.prototype.transpose = function () {
        if (this.isOk() && this.inner() == null)
            return null;
        else
            return this;
    };
    /**
     * Returns `res` if the {@link Result} is `Ok`, otherwise returns the inner `Err` value of the {@link Result}
     *
     * @example
     * ```ts
     * const a: Result.Ok(10);
     * const b: Result.Err('foo');
     *
     * console.log(a.and(v => Result.Ok(v * 2))); // Ok(20)
     * console.log(v.and(v => Result.Ok(v * 2))); // Err('foo')
     * ```
     */
    Result.prototype.and = function (res) {
        if (this.isErr())
            return this;
        else
            return res;
    };
    /**
     * If `this` is `Ok`, return `this`. Otherwise, return the other given {@link Result}, `res`;
     *
     * @example
     * ```ts
     * function div(n1: number, n2: number) {
     *  if (n2 == 0) return Result.Err('divide by zero');
     *  else return Result.Ok(n1 / n2);
     * }
     *
     * console.log(div(10, 2).or(Result.Ok(0))) // Ok(5)
     * console.log(div(10, 0).or(Result.Ok(0))) // Ok(0)
     * ```
     */
    Result.prototype.or = function (res) {
        if (this.isOk())
            return this;
        else
            return res;
    };
    /**
     * Calls and returns the value of `fn` if `this` is `Ok`, otherwise returns `this`;
     *
     * @example
     * ```ts
     * function div(n1: number, n2: number) {
     *  if (n2 == 0) return Result.Err('divide by zero');
     *  else return Result.Ok(n1 / n2);
     * }
     *
     * console.log(div(20, 2).andThen(n => div(n, 2))); // Ok(5)
     * console.log(div(20, 0).andThen(n => div(n, 2))); // Err('divide by zero');
     * ```
     */
    Result.prototype.andThen = function (fn) {
        if (this.isErr())
            return Result.Err(this.inner());
        else
            return fn(this.inner());
    };
    /**
     * Calls and returns the value of `fn` if the {@link Result} is an `Err`, otherwise returns the `Ok` value of `this`.
     *
     * @example
     * ```ts
     * const square = (v: number) => Result.Ok(v * v);
     * const err = (v: number) => Result.Err(v);
     *
     * console.log(Result.Ok(2).orElse(square)); // Ok(2)
     * console.log(Result.Err(2).orElse(square)); // Ok(4)
     * console.log(Result.Ok(3).orElse(square).orElse(err)) // Ok(9)
     * ```
     */
    Result.prototype.orElse = function (fn) {
        if (this.isOk())
            return Result.Ok(this.inner());
        else
            return fn(this.inner());
    };
    /**
     * Returns the contained `Ok` value of the {@link Result}. If the {@link Result} in an `Err`,
     * throws a generic error.
     *
     * @example
     * ```ts
     * console.log(Result.Ok('foo').unwrap()); // 'foo'
     * console.log(Result.Err('bar').unwrap()); // ERROR!
     * ```
     */
    Result.prototype.unwrap = function () {
        if (this.isErr())
            throw "attempted to unwrap ERR result";
        else
            return this.inner();
    };
    /**
     * Returns the contained `Ok` value of the {@link Result}. If the {@link Result} is an `Err`,
     * then returns the given value `v`.
     *
     * @example
     * ```ts
     * const a = Result.Err('foo');
     * const b = Result.Ok('fizz');
     *
     * console.log(a.unwrapOr('bar')); // 'bar'
     * console.log(b.unwrapOr('bar')); // 'fizz'
     * ```
     */
    Result.prototype.unwrapOr = function (v) {
        if (this.isOk())
            return this.inner();
        else
            return v;
    };
    /**
     * Returns the contained `Ok` value of the {@link Result}. If the {@link Result} is an `Err`,
     * then returns the result of the given `fn`, called with the `Err` value.
     *
     * @example
     * ```ts
     * const a = Result.Ok(15);
     * const b = Result.Err(15);
     *
     * console.log(a.unwrapOrElse(err => 'some value')); // 15
     * console.log(b.unwrapOrElse(err => err.toString())); // '15'
     * ```
     */
    Result.prototype.unwrapOrElse = function (fn) {
        if (this.isOk())
            return this.inner();
        else
            return fn(this.inner());
    };
    /**
     * Returns `true` if the {@link Result} `Ok` state equals the given value.
     *
     * @example
     * ```ts
     * console.log(Result.Ok('foo').contains('foo')); // true
     * console.log(Result.Ok('foobar').contains('foo')); // false
     * console.log(Result.Err('foo').contains('foo')) // false
     * ```
     */
    Result.prototype.contains = function (v) {
        if (this.isErr())
            return false;
        else
            return this.inner() == v;
    };
    /**
     * Returns `true` if the {@link Result} `Err` value equals the given value.
     *
     * @example
     * ```ts
     * console.log(Result.Ok('foo').contains('foo')); // true
     * console.log(Result.Ok('foobar').contains('foo')); // false
     * console.log(Result.Err('foo').contains('foo')) // false
     * ```
     */
    Result.prototype.containsErr = function (v) {
        if (this.isOk())
            return false;
        else
            return this.inner() == v;
    };
    Result.prototype.equals = function (res) {
        return res.isOk() == this.isOk() && res.inner() == this.inner();
    };
    Result.prototype.toString = function () {
        return "".concat(this.isOk() ? "Ok" : "Err", "(").concat(JSON.stringify(this.inner()), ")");
    };
    return Result;
}());
exports.Result = Result;
